/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import ImportColumnLayout from '../layout/ImportColumnLayout';
import { classNames } from '../../utilities/styleUtils';
import { formatDateWithTimezone } from '../../utilities/formatting';
import { timezoneOptions, regionOptions, dateTimeFormat } from '../../utilities/constants';
import { MagnifyingGlassIcon, BoltIcon, LockClosedIcon, TicketIcon, BeakerIcon } from '@heroicons/react/24/outline';
import Button from '../controls/Button';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchEventProjects, selectEventProjects, selectEventProjectsStatus } from '../../reducers/projectsSlice';
import { useToken } from '../../hooks/useToken';
import { Input } from '../controls/Input';
import MarkdownEditor from '../controls/MarkdownEditor';
import { defaultInstructions } from './defaultInstructions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import {
  CalendarDaysIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  FingerPrintIcon,
  HashtagIcon,
  MapPinIcon,
  GlobeAltIcon,
} from '@heroicons/react/20/solid';
import EventTimeline from '../timeline/EventTimeline';
import { type DateValueType } from 'react-tailwindcss-datepicker';
import InputDatepicker from '../controls/InputDatepicker';
import axios from 'axios';
import { getConfig } from '../../config/config-helper';
import { InputSelect, type SelectOption } from '../controls/InputSelect';
import { salesForceIdValidation, ValidationLevel, validateEventName } from '../../utilities/validation';
import Switch from '../controls/Switch';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface EventCreateRequest {
  name: string;
  location: string;
  build_date: string;
  start_date: string;
  end_date: string;
  decommission_date: string;
  grant_reporter_access_to_configure_project: boolean;
  organization_account_identifier: string;
  extra_env_vars: Record<string, string>;
  instructions: string;
  initial_pool_size: number;
  dataops_configure_project_path: string;
  dataops_configure_project_pipe_file: string;
  dataops_configure_project_git_ref: string;
  dataops_top_level_group_path: string;
  snowflake_account_region_group: string;
  snowflake_account_edition: string;
  allowed_email_domains?: string[];
}
export default function NewEventSolution() {
  const { dataopsAdminAppApiEndpoint, snowflakeOrganizationAccounts } = getConfig();
  const { canPublish } = useCurrentUser();
  const role = canPublish ? 'admin' : 'consumer';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useToken();
  const minDate = new Date();
  minDate.setDate(minDate.getDate() - 1);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 365);
  const dateSeed = {
    startDate: null,
    endDate: null,
  };
  const [buildDateValue, setBuildDateValue] = useState<DateValueType>(dateSeed);
  const [eventDateValue, setEventDateValue] = useState<DateValueType>(dateSeed);
  const [deleteDateValue, setDeleteDateValue] = useState<DateValueType>(dateSeed);

  const buildDateMaxDate = eventDateValue?.startDate ? new Date(eventDateValue.startDate) : maxDate;

  const deleteMinDate = eventDateValue?.endDate ? new Date(eventDateValue.endDate) : minDate;

  const [eventName, setEventName] = useState('');
  const [eventNameError, setEventNameError] = useState('');
  const [eventSlug, setEventSlug] = useState('');
  const [slugError, setSlugError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [eventLocation, setEventLocation] = useState('Virtual');
  const deliveryFormatOptions: SelectOption[] = [
    {
      id: 'WORKSHOP',
      name: 'Workshop',
      description:
        'A structured group session focused on interactive learning, where attendees engage in discussions, activities, and collaborative projects, often facilitated by an instructor.',
    },
    {
      id: 'HANDS_ON_LAB',
      name: 'Hands On Lab',
      description:
        'A practical session that emphasizes experiential learning, allowing attendees to apply theoretical knowledge through direct manipulation of tools, technologies, or processes in a controlled environment.',
    },
    {
      id: 'TRAINING',
      name: 'Training',
      description:
        "A formal program aimed at enhancing attendees' skills and knowledge in a specific area, which may include lectures, demonstrations, and hands-on practice, often with a focus on professional development.",
    },
    {
      id: 'HACKATHON',
      name: 'Hackathon',
      description:
        'A collaborative event where participants work together to solve problems, build solutions, or create new applications within a limited timeframe, often with a competitive element.',
    },
    {
      id: 'OTHER',
      name: 'Other',
      description:
        'Any session type that does not fit into the predefined categories, allowing to specify alternative formats or experiences for attendees.',
    },
  ];
  const [deliveryFormat, setDeliveryFormat] = useState<SelectOption>({ id: 'HANDS_ON_LAB', name: 'Hands On Lab' });
  const [totalAttendees, setTotalAttendees] = useState('');
  const [instructorAccounts, setInstructorAccounts] = useState('');
  const [salesForceId, setSalesForceId] = useState('');
  const [salesForceIdError, setSalesForceIdError] = useState('');

  // Domain restriction state
  const [snowflakeOnlyToggle, setSnowflakeOnlyToggle] = useState(false);
  const [showAdvancedDomains, setShowAdvancedDomains] = useState(false);
  const [customDomains, setCustomDomains] = useState('');

  // Sync snowflakeOnlyToggle with customDomains field
  useEffect(() => {
    const domains = customDomains
      .split(',')
      .map((d) => d.trim())
      .filter((d) => d.length > 0);

    const hasSnowflakeDomain = domains.includes('snowflake.com');

    if (snowflakeOnlyToggle && !hasSnowflakeDomain) {
      // Add snowflake.com to the list
      const updatedDomains = [...domains, 'snowflake.com'];
      setCustomDomains(updatedDomains.join(', '));
    } else if (!snowflakeOnlyToggle && hasSnowflakeDomain) {
      // Remove snowflake.com from the list
      const updatedDomains = domains.filter((d) => d !== 'snowflake.com');
      setCustomDomains(updatedDomains.join(', '));
    }
  }, [snowflakeOnlyToggle]);

  const updateInstructions = useCallback((name: string) => {
    let updatedInstructions = defaultInstructions;

    updatedInstructions = updatedInstructions.replace('EVENT_NAME_PLACEHOLDER', name ?? '{{ vars.EVENT_NAME }}');

    return updatedInstructions;
  }, []);

  const [instructions, setInstructions] = useState(updateInstructions(''));

  // Get solutions from Redux store
  const eventProjects = useAppSelector(selectEventProjects);
  const eventProjectsStatus = useAppSelector(selectEventProjectsStatus);
  const [selectedSolution, setSelectedSolution] = useState<SelectOption | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for solution parameter in URL on first load
  useEffect(() => {
    const solutionId = searchParams.get('solution');
    if (solutionId && eventProjects.length > 0) {
      const solution = eventProjects.find((project) => project.id === solutionId);
      if (solution) {
        setSelectedSolution(solution);
      }
    }
  }, []);

  // Update solution parameter in URL when selectedSolution changes
  useEffect(() => {
    const currentSolutionId = searchParams.get('solution');
    // Only update URL if the current parameter doesn't match the selected solution
    if (selectedSolution && currentSolutionId !== selectedSolution.id) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('solution', selectedSolution.id);
      setSearchParams(newSearchParams);
    } else if (!selectedSolution && currentSolutionId) {
      // If no solution is selected but URL has a solution parameter, remove it
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('solution');
      setSearchParams(newSearchParams);
    }
  }, [selectedSolution]);

  useEffect(() => {
    if (eventName) {
      setInstructions(updateInstructions(eventName));
    }
  }, [eventName, selectedSolution, updateInstructions]);

  // Set initial build date to 1 day before start date when start date is selected
  useEffect(() => {
    if (eventDateValue?.startDate && buildDateValue?.startDate === null) {
      const buildDate = new Date(eventDateValue.startDate);
      buildDate.setDate(buildDate.getDate() - 1);
      console.log('Setting buildDate', buildDate);
      setBuildDateValue({
        startDate: buildDate,
        endDate: buildDate,
      });
    }
  }, [eventDateValue?.startDate]);

  // Set initial delete date to 2 days after event end date when event end date is selected
  useEffect(() => {
    if (eventDateValue?.endDate && deleteDateValue?.startDate === null) {
      const deleteDate = new Date(eventDateValue.endDate);
      deleteDate.setDate(deleteDate.getDate() + 2);
      console.log('Setting deleteDate', deleteDate);
      setDeleteDateValue({
        startDate: deleteDate,
        endDate: deleteDate,
      });
    }
  }, [eventDateValue?.endDate]);

  useEffect(() => {
    validateDates();
  }, [buildDateValue?.startDate, deleteDateValue?.startDate, eventDateValue?.endDate, eventDateValue?.startDate]);

  const [solutionsError, setSolutionsError] = useState<string | undefined>(undefined);

  const getTimezoneOffset = (tzId: string): string => {
    return dayjs().tz(tzId).format('Z');
  };
  const [timezone, setTimezone] = useState<SelectOption>(timezoneOptions[0]);

  const [accountRegion, setAccountRegion] = useState<SelectOption>(regionOptions[0]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [buildDateError, setBuildDateError] = useState<string>();
  const [deleteDateError, setDeleteDateError] = useState<string>();

  const generateSlug = (name: string) => {
    // Generate the slug and then truncate to max 31 characters
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 31); // Limit to 31 characters max to prevent pipeline failures
  };

  const validateSlug = useCallback(
    async (slug: string) => {
      if (!slug) return;
      setIsValidating(true);
      try {
        await axios.get(`${dataopsAdminAppApiEndpoint}/event_management/${slug}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        setSlugError('This event ID already exists');
      } catch (error: any) {
        if (error.response?.status === 404) {
          setSlugError('');
        } else {
          setSlugError('Error checking event ID');
        }
      } finally {
        setIsValidating(false);
      }
    },
    [token],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (eventSlug) {
        void validateSlug(eventSlug);
      }
    }, 500);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [eventSlug, validateSlug]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setEventName(name);
    setEventSlug(generateSlug(name));
    setEventNameError(validateEventName(name) ?? '');
  };

  useEffect(() => {
    if (token) {
      // Dispatch the Redux thunk to fetch event projects
      dispatch(fetchEventProjects(token))
        .unwrap()
        .catch((error: any) => {
          console.error('Failed to fetch solutions:', error);
          setSolutionsError(error.message || 'Failed to fetch solutions');
        });
    }
  }, [token, dispatch]);

  const validateDates = () => {
    setBuildDateError(undefined);
    setDeleteDateError(undefined);

    if (buildDateValue?.startDate && eventDateValue?.startDate) {
      // create dates with time removed
      const buildDate = new Date(buildDateValue.startDate);
      buildDate.setHours(0, 0, 0, 0);
      const eventStartDate = new Date(eventDateValue.startDate);
      eventStartDate.setHours(0, 0, 0, 0);

      if (buildDate > eventStartDate) {
        setBuildDateError('Build date must be before or equal to event start date');
        return false;
      }
    }

    if (deleteDateValue?.startDate && eventDateValue?.endDate) {
      // create dates with time removed
      const deleteDateMinusOne = new Date(deleteDateValue.startDate);
      deleteDateMinusOne.setHours(0, 0, 0, 0);
      deleteDateMinusOne.setDate(deleteDateMinusOne.getDate() - 1);
      const eventEndDate = new Date(eventDateValue.endDate);
      eventEndDate.setHours(0, 0, 0, 0);

      if (deleteDateMinusOne < eventEndDate) {
        setDeleteDateError('Delete date must be at least 1 day after event end date');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    setError(undefined);
    setIsSubmitting(true);

    // Validate required fields
    if (!eventName) {
      setError('Event name is required');
      setIsSubmitting(false);
      return;
    }
    const eventNameValidationError = validateEventName(eventName);
    if (eventNameValidationError) {
      setError(eventNameValidationError);
      setIsSubmitting(false);
      return;
    }
    if (
      !buildDateValue?.startDate ||
      !eventDateValue?.startDate ||
      !eventDateValue?.endDate ||
      !deleteDateValue?.startDate
    ) {
      setError('All dates are required');
      setIsSubmitting(false);
      return;
    }
    if (!totalAttendees) {
      setError('Number of attendees is required');
      setIsSubmitting(false);
      return;
    }
    if (instructorAccounts && isNaN(parseInt(instructorAccounts))) {
      setError('Instructor accounts must be a valid number');
      setIsSubmitting(false);
      return;
    }

    if (!validateDates()) {
      setIsSubmitting(false);
      return;
    }

    if (!selectedSolution) {
      setError('Please select a solution');
      setIsSubmitting(false);
      return;
    }

    // Validate the total pool size doesn't exceed 500 accounts
    const totalPoolSize = parseInt(totalAttendees) + parseInt(instructorAccounts || '0');
    if (totalPoolSize > 500) {
      setError('The maximum number of accounts (attendees + instructors) cannot exceed 500');
      setIsSubmitting(false);
      return;
    }

    try {
      const buildDateTime = formatDateWithTimezone(buildDateValue.startDate, 5, timezone.id);
      const startDateTime = formatDateWithTimezone(eventDateValue.startDate, 7, timezone.id);
      const endDateTime = formatDateWithTimezone(eventDateValue.endDate, 22, timezone.id);
      const decommissionDateTime = formatDateWithTimezone(deleteDateValue.startDate, 23, timezone.id);

      const solutionId = selectedSolution.id;
      const solutionPath = selectedSolution.path;

      // Build allowed email domains array
      const allowedDomains: string[] = [];

      // Add snowflake.com if toggle is enabled
      if (snowflakeOnlyToggle) {
        allowedDomains.push('snowflake.com');
      }

      // Add custom domains if provided
      if (customDomains.trim()) {
        const customDomainsList = customDomains
          .split(',')
          .map((d) => d.trim())
          .filter((d) => d.length > 0);

        // Add custom domains, avoiding duplicates
        customDomainsList.forEach((domain) => {
          if (!allowedDomains.includes(domain)) {
            allowedDomains.push(domain);
          }
        });
      }

      const requestBody: EventCreateRequest = {
        name: eventName,
        location: eventLocation,
        build_date: buildDateTime,
        start_date: startDateTime,
        end_date: endDateTime,
        decommission_date: decommissionDateTime,
        grant_reporter_access_to_configure_project: true,
        organization_account_identifier: snowflakeOrganizationAccounts[0],
        extra_env_vars: {
          DATAOPS_CATALOG_SALESFORCE_ID: salesForceId,
          INSTRUCTOR_ACCOUNTS: instructorAccounts || '0',
          SELECTED_SOLUTION: solutionId,
        },
        instructions,
        initial_pool_size: totalPoolSize,
        dataops_configure_project_path: solutionPath ?? '',
        dataops_configure_project_pipe_file: 'full-ci.yml',
        dataops_configure_project_git_ref: 'main',
        dataops_top_level_group_path: 'snowflake/event',
        snowflake_account_region_group: accountRegion.id,
        snowflake_account_edition: 'ENTERPRISE',
      };

      // Only add allowed_email_domains if there are domains to add
      if (allowedDomains.length > 0) {
        requestBody.allowed_email_domains = allowedDomains;
      }

      await axios.post(`${dataopsAdminAppApiEndpoint}/event_management/${eventSlug}`, requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'SSC-Role': role,
        },
      });

      navigate(`/event-deployments/${eventSlug}`);
    } catch (err: any) {
      console.error('Failed to create event:', err);
      setError(err.response?.data?.message || 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  return canPublish ? (
    <ImportColumnLayout>
      <div>
        <span
          className={classNames(
            'text-dataops-primary-light-blue',
            'inline-flex rounded-lg p-3 ring-1 ring-dataops-primary-light-blue',
          )}
        >
          <BoltIcon aria-hidden="true" className="h-6 w-6" />
        </span>
      </div>
      <h1 className="text-xl">New event</h1>
      <div className="flex space-x-1">
        <div className="inline-flex items-center px-1 space-x-1 text-sm text-gray-800 rounded-md ring-1 ring-inset cursor-default ring-gray-500/10">
          <LockClosedIcon className="w-4 h-4" /> <span>Administrators only</span>
        </div>
      </div>
      <div className="space-y-20">
        <SolutionNewSection title="Event Details">
          <Input
            id="eventNameInput"
            data-testid="event-name-input"
            label="Event name"
            icon={TicketIcon}
            value={eventName}
            onChange={handleNameChange}
            error={eventNameError || undefined}
            help={<p>Your headline name for the event for attendees to recognize.</p>}
          />
          <Input
            id="eventSlugInput"
            data-testid="event-slug-input"
            label="Event unique ID"
            icon={FingerPrintIcon}
            value={eventSlug}
            onChange={(e) => {
              setEventSlug(generateSlug(e.target.value));
            }}
            error={slugError}
            loading={isValidating}
            help={
              <p>
                Used by attendees to get the correct event landing page (go.dataops.live/{eventSlug}).
                {eventSlug.length >= 31 && (
                  <>
                    <br />
                    <span className="text-blue-500">
                      Limited to 31 characters due to Snowflake account name max length.
                    </span>
                  </>
                )}
              </p>
            }
          />
          <InputSelect
            id="accountRegionInput"
            data-testid="account-region-input"
            label="Account Region"
            icon={GlobeAltIcon}
            options={regionOptions}
            value={accountRegion}
            onChange={setAccountRegion}
            help={<p>Select the region where Snowflake accounts will be provisioned.</p>}
          />
          <Input
            id="eventLocationInput"
            data-testid="event-location-input"
            label="Location"
            icon={MapPinIcon}
            value={eventLocation}
            onChange={(e) => {
              setEventLocation(e.target.value);
            }}
            placeholder="Virtual"
            help={<p>Used on the event landing page and can be used in event documentation.</p>}
          />
          <InputSelect
            id="eventDeliveryFormatInput"
            data-testid="event-delivery-format-input"
            label="Delivery format"
            icon={ChatBubbleLeftRightIcon}
            options={deliveryFormatOptions}
            value={deliveryFormat}
            onChange={setDeliveryFormat}
          />
          <InputSelect
            id="eventSolutionInput"
            data-testid="event-solution-input"
            label="Solution"
            icon={MagnifyingGlassIcon}
            options={eventProjects}
            value={selectedSolution}
            onChange={setSelectedSolution}
            loading={eventProjectsStatus === 'loading'}
            error={solutionsError ?? undefined}
            searchable={true}
            help={
              <div>
                <p>Select the project to be used for this event.</p>
                {selectedSolution && (
                  <p>
                    <i>
                      <b>{selectedSolution?.path}</b>
                    </i>
                  </p>
                )}
                {eventProjectsStatus === 'loading' && (
                  <p className="text-gray-500 italic">Loading solutions from GitLab...</p>
                )}
                {eventProjects.length === 0 && eventProjectsStatus !== 'loading' && !solutionsError && (
                  <p className="text-gray-500 italic">No solutions found in the snowflake/event namespace.</p>
                )}
              </div>
            }
          />
          <InputSelect
            id="eventTimezoneInput"
            data-testid="event-timezone-input"
            label="Timezone"
            icon={GlobeAltIcon}
            options={timezoneOptions}
            value={timezone}
            onChange={setTimezone}
            help={<p>All event times will be in this timezone. Current offset: UTC{getTimezoneOffset(timezone.id)}</p>}
          />
          <InputDatepicker
            id="eventStartEndDateInput"
            dataTestid="event-start-end-date-input"
            label="When event starts and finishes"
            dateValue={eventDateValue}
            setDateValue={(value) => {
              setEventDateValue(value);
            }}
            asSingle={false}
            useRange={true}
            minDate={minDate}
            maxDate={maxDate}
            icon={CalendarDaysIcon}
          />
          <InputDatepicker
            id="eventBuildDateInput"
            dataTestid="event-build-date-input"
            label="When to build Snowflake accounts"
            dateValue={buildDateValue}
            setDateValue={(value) => {
              setBuildDateValue(value);
              setBuildDateError(undefined);
            }}
            error={buildDateError}
            asSingle={true}
            useRange={false}
            minDate={minDate}
            maxDate={buildDateMaxDate}
            icon={CalendarDaysIcon}
            help={
              <div>
                <p>You can delay the Snowflake account creation until this date.</p>
                {eventDateValue?.startDate && (
                  <p className="text-gray-500 mt-1">
                    Selected:{' '}
                    {buildDateValue?.startDate ? new Date(buildDateValue.startDate).toLocaleDateString() : 'not set'}
                  </p>
                )}
              </div>
            }
          />
          <InputDatepicker
            id="eventDeleteDateInput"
            dataTestid="event-delete-date-input"
            label="When to delete Snowflake accounts"
            dateValue={deleteDateValue}
            setDateValue={(value) => {
              setDeleteDateValue(value);
              setDeleteDateError(undefined);
            }}
            error={deleteDateError}
            asSingle={true}
            useRange={false}
            minDate={deleteMinDate}
            maxDate={maxDate}
            icon={CalendarDaysIcon}
            help={
              <div>
                <p>You can delay the Snowflake account deletion until this date.</p>
                {eventDateValue?.endDate && (
                  <p className="text-gray-500 mt-1">
                    Selected:{' '}
                    {deleteDateValue?.startDate ? new Date(deleteDateValue.startDate).toLocaleDateString() : 'not set'}
                  </p>
                )}
              </div>
            }
          />
          <Input
            id="eventTotalAttendeesInput"
            data-testid="event-total-attendees-input"
            label="Maximum expected number of attendees"
            icon={HashtagIcon}
            type="number"
            min={1}
            value={totalAttendees}
            onChange={(e) => {
              setTotalAttendees(e.target.value);
            }}
            suggestions={['10', '25', '50', '100']}
            onSuggestionClick={(value) => {
              setTotalAttendees(value);
            }}
          />
          <Input
            id="eventInstructorAccountsInput"
            data-testid="event-instructor-accounts-input"
            label="Maximum expected instructor practice accounts"
            icon={HashtagIcon}
            type="number"
            min={0}
            value={instructorAccounts}
            onChange={(e) => {
              setInstructorAccounts(e.target.value);
            }}
            suggestions={['1', '2', '5', '10']}
            onSuggestionClick={(value) => {
              setInstructorAccounts(value);
            }}
            help={
              <p>
                Total Snowflake accounts to be created:{' '}
                {parseInt(totalAttendees || '0') + parseInt(instructorAccounts || '0')}
              </p>
            }
          />
          <Input
            id="salesforceIdInput"
            data-testid="event-salesforce-id-input"
            label="Salesforce ID"
            icon={ChartPieIcon}
            optional
            help={<p>Enter a Salesforce ID to help report on event activity related to a customer.</p>}
            value={salesForceId}
            onChange={(e) => {
              const value = e.target.value;
              setSalesForceId(value);
              setSalesForceIdError(salesForceIdValidation(value, ValidationLevel.AllowEmpty));
            }}
            error={salesForceIdError}
          />
        </SolutionNewSection>
        <SolutionNewSection title="Access Restriction">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Switch enabled={snowflakeOnlyToggle} setEnabled={setSnowflakeOnlyToggle} />
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-900">Snowflake.com emails only</label>
                <p className="text-sm text-gray-500">Restrict event registration to @snowflake.com email addresses</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setShowAdvancedDomains(!showAdvancedDomains);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showAdvancedDomains ? 'Hide' : 'Show'} Advanced Domain Controls
              </button>
            </div>

            {showAdvancedDomains && (
              <Input
                id="customDomainsInput"
                data-testid="custom-domains-input"
                label="Allowed Email Domains"
                value={customDomains}
                onChange={(e) => {
                  setCustomDomains(e.target.value);
                }}
                placeholder="example.com, partner.org"
                help={<p>Enter comma-separated domain names.</p>}
              />
            )}
          </div>
        </SolutionNewSection>
        <SolutionNewSection title="Instructions">
          <MarkdownEditor
            id="eventInstructionsInput"
            testId="event-instructions-input"
            label="Event Instructions"
            required
            value={instructions}
            eventName={eventName}
            onChange={setInstructions}
            help={<p>Enter markdown instructions for the event. This will be displayed on the event landing page.</p>}
          />
        </SolutionNewSection>
        <div id="controls" className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          <EventTimeline
            buildDateValue={buildDateValue}
            eventDateValue={eventDateValue}
            deleteDateValue={deleteDateValue}
            timezone={timezone}
          />

          {error && <div className="text-sm text-red-600 bg-red-50 p-4 rounded-md">{error}</div>}
          <div className="flex space-x-4">
            <Button
              id="backButton"
              data-testid="event-back-button"
              intent={'light'}
              onClick={() => {
                navigate(-1);
              }}
            >
              Back
            </Button>
            <Button
              id="createButton"
              data-testid="event-create-button"
              disabled={!canPublish || isSubmitting}
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              Create
            </Button>
          </div>
        </div>
      </div>
    </ImportColumnLayout>
  ) : (
    <ImportColumnLayout>
      <div>
        <span
          className={classNames(
            'text-dataops-primary-light-blue',
            'inline-flex rounded-lg p-3 ring-1 ring-dataops-primary-light-blue',
          )}
        >
          <BoltIcon aria-hidden="true" className="h-6 w-6" />
        </span>
      </div>
      <h1 className="text-xl">New event</h1>
      <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <LockClosedIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              You do not have sufficient permissions to create events. Only administrators can create new events.
            </p>
          </div>
        </div>
      </div>
    </ImportColumnLayout>
  );
}

function SolutionNewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-center justify-center">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="mx-4 text-center max-w-xs text-lg text-gray-400 hover:cursor-default">{title}</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
